import { SsoType } from '@diritto/common/account/constants/sso.constant';
import { FontWeight } from '@diritto/common/style/typography';
import { buildUrl } from '@diritto/common/utils/urls';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ButtonColor, ButtonHeight, ButtonSize } from 'constants/button';
import { LoginButtonType, LoginIcon } from 'constants/login';
import { DirittoAppBridge } from 'libs/DirittoAppBridge';
import { useRouter } from 'next/navigation';
import { useCallback, useMemo } from 'react';
import { configure } from 'utils/configure';
import { LoginBasePath, LoginButtonLabel } from '@diritto/common/account/constants/login.constant';
import { useSignAppNative } from 'state-hooks/accounts/useSignAppNative';

interface LoginButtonProps {
  ssoType: SsoType;
  normalizedReturnUrl?: string;
}

export const LoginButton = ({ ssoType, normalizedReturnUrl }: LoginButtonProps) => {
  const router = useRouter();
  const [buttonType, label, Icon] = [LoginButtonType[ssoType], LoginButtonLabel[ssoType], LoginIcon[ssoType]];
  const { backgroundColor, color, borderColor } = useMemo(() => ButtonColor[buttonType], [buttonType]);
  const isUseSignAppNative = useSignAppNative();

  const handleClick = useCallback(() => {
    if (isUseSignAppNative(ssoType)) {
      DirittoAppBridge.requestSign(ssoType, false);
    } else {
      const baseUrl =
        ssoType === SsoType.Email ? '/accounts/sign-in/email' : `${configure.apiPublicUrl}${LoginBasePath[ssoType]}`;
      router.push(buildUrl(baseUrl, { returnUrl: normalizedReturnUrl }));
    }
  }, [isUseSignAppNative, ssoType, router, normalizedReturnUrl]);

  return (
    <StyledButtonWithIconAndText
      type="button"
      backgroundColor={backgroundColor}
      color={color}
      borderColor={borderColor}
      onClick={handleClick}>
      <IconWrapper>
        <Icon />
      </IconWrapper>
      {label}
    </StyledButtonWithIconAndText>
  );
};

const StyledButtonWithIconAndText = styled.button(
  ({ backgroundColor, color, borderColor }: { backgroundColor: string; color: string; borderColor: string }) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;

    background-color: ${backgroundColor};
    border: 1px solid ${borderColor};
    color: ${color};
    border-radius: 4px;
    height: ${ButtonHeight[ButtonSize.Large]}px;
    white-space: nowrap;
    font-weight: ${FontWeight.Bold};

    &:active {
      filter: brightness(0.9);
    }

    & > *:first-of-type {
      margin-right: 10px;
    }
  `,
);

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
`;
